import axios, { AxiosResponse } from 'axios'
import { Session } from 'next-auth'

import { Chat } from '#/shared/api/type-model-chats'
import { Error } from '#/shared/lib/types/entities'
import { IImagesResponse } from '#/shared/lib/types/types-dalle'
import { IMessageRequest, ISendMessageResponse } from '#/shared/lib/types/types-gpt'

import { Message } from '../lib/types/model'

import { getApiUrl } from '#/shared/lib/constants'

type Result = {
	question: string
	link: string
	created_at: string
	resulting_balance?: number
}

export type Wrap = {
	count: number
	next: string | null
	page_size: number
	previous: string | null
}

export type WrapResponse<T> = Wrap & {
	results: T[]
}

type INeuronModel = any

export const api = {
	async getImagesSD(token: string | null, url: string): Promise<WrapResponse<Result> | null> {
		try {
			const { data } = await axios.get(url, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			return data
		} catch (err) {
			return null
		}
	},

	async getImagesDalle(token: string | null, url: string): Promise<WrapResponse<IImagesResponse> | null> {
		try {
			const { data } = await axios.get(url, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})

			return data
		} catch (err) {
			return null
		}
	},

	async getMessagesChatGPT(token: string | null, uid: string): Promise<Message[] | Error> {
		try {
			const { data } = await axios.get<Message[]>(getApiUrl() + `/chats/${uid}/messages/`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			return data
		} catch (err: any) {
			return {
				error: true,
				message: 'Произошла ошибка при выполнении запроса',
				details: err.message,
			}
		}
	},

	async updRenameChat(uid: string, title: string, token: string | undefined) {
		try {
			const { data } = await axios.put<{ uid: string; title: string; created_at: string }>(
				getApiUrl() + `/chats/${uid}/`,
				{ title: title },
				{
					withCredentials: true,
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			)
			return { data, isError: false }
		} catch (err) {
			return { data: null, isError: true, error: err }
		}
	},

	async sendMessageChatGPT(token: string, message: any, uid: string): Promise<ISendMessageResponse> {
		try {
			const { data } = await axios.post<IMessageRequest, AxiosResponse<Message[]>>(
				getApiUrl() + `/chats/${uid}/messages/`,
				message,
				{
					withCredentials: true,
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			)

			return { data, isError: false }
		} catch (err) {
			return { data: null, isError: true, error: err }
		}
	},

	async getFavoritesModel(token: string | null, session: Session | null): Promise<INeuronModel[] | []> {
		try {
			const { data } = await axios.get<INeuronModel[] | []>(getApiUrl() + `/ml_models/${session?.user?.name}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})

			return data?.filter((model) => model.is_favourite)
		} catch (err) {
			return []
		}
	},

	async getAllModels(token: string | null): Promise<INeuronModel[] | []> {
		try {
			const { data } = await axios.get<INeuronModel[]>(getApiUrl() + '/ml_models/', {
				headers: {
					Authorization: token ? `Bearer ${token}` : '',
				},
			})
			

			return data
		} catch (err) {
			return []
		}
	},
}

export const createChat = async (model: string, token?: string) => {
	try {
		const { data } = await axios.post(
			getApiUrl() + `/chats/?model=${model}`,
			{
				title: 'Новый чат',
				model,
			},
			{ headers: { Authorization: `Bearer ${token}` } }
		)

		return data
	} catch (e) {
		return null
	}
}

export const getAllChats = async (model: string, token?: string): Promise<Chat[] | []> => {
	try {
		const { data } = await axios.get(getApiUrl() + `/chats/?model=${model}`, {
			headers: { Authorization: `Bearer ${token}` },
		})

		if (data.length === 0) {
			const newChat = await createChat(model, token)

			return Array(newChat)
		}

		return data
	} catch (e) {
		return []
	}
}

export const addToFavorites = async (model_id: string, token?: string) => {
	try {
		const { data, status } = await axios.put(
			getApiUrl() + '/ml_models/favourites ',
			{
				uid: model_id,
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		)
		return status
	} catch (err) {
		return 400
	}
}

export const deleteFromFavorites = async (model_id: string, token?: string) => {
	try {
		const { status, data } = await axios.delete(getApiUrl() + '/ml_models/favorites', {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			data: {
				uid: model_id,
			},
		})
		return status
	} catch (err) {
		return 400
	}
}

export interface PaymentHistory {
	uid: string
	plan: {
		uid: string
		title: string
		price: string
		tokens_per_plan: string
		duration: string
	}
	created_at: string
	updated_at: string
	amount: string
	status: string
}

export const getPaymentsHistory = async (token?: string) => {
	try {
		const { data } = await axios.get<PaymentHistory[]>(getApiUrl() + '/payments/history', {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		return data
	} catch (err) {
		return null
	}
}

export interface IReferral {
	referrals: [
		{
			email: string
			joined_at: string
			profile_picture_link: string
		}
	]
	registrations_count: number
	payments_count: number
	accrued_bonuses_amount: number
}

export const getReferral = async (token?: string) => {
	try {
		const { data } = await axios.get<IReferral>(getApiUrl() + '/payments/referral-account', {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		return data
	} catch (err) {
		return null
	}
}
